import { useState } from 'react';
import type { DatabaseInfo, SchemaTable } from '../worker/protocol';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';

interface DatabaseSidebarProps {
  databases: DatabaseInfo[];
  activeDb: string | null;
  schema: SchemaTable[];
  schemaLoading: boolean;
  canManage: boolean;
  onSwitch: (name: string) => void;
  onCreate: (name: string) => void;
  onRename: (oldName: string, newName: string) => void;
  onDelete: (name: string) => void;
  onExportDb: (name: string) => void;
  onRefresh: () => void;
  onPickTable: (name: string) => void;
  onSelectFromTable: (name: string) => void;
  onCountTable: (name: string) => void;
  onShowDdl: (table: SchemaTable) => void;
  onDropTable: (name: string) => void;
  onInsertSnippet: (text: string) => void;
}

function sanitizeName(input: string): string {
  return input
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 64);
}

function TableIcon({ kind }: { kind: 'table' | 'view' }) {
  if (kind === 'view') {
    return (
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <circle cx="6" cy="6" r="2" fill="none" stroke="#9876aa" strokeWidth="1.2" />
        <path
          d="M1 6 C2.5 3 4 2 6 2 C8 2 9.5 3 11 6 C9.5 9 8 10 6 10 C4 10 2.5 9 1 6 Z"
          fill="none"
          stroke="#9876aa"
          strokeWidth="1.2"
        />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <rect
        x="1.5"
        y="2"
        width="9"
        height="8"
        rx="1"
        fill="none"
        stroke="#5394ec"
        strokeWidth="1.2"
      />
      <line x1="1.5" y1="5" x2="10.5" y2="5" stroke="#5394ec" strokeWidth="1" />
      <line x1="5" y1="2" x2="5" y2="10" stroke="#5394ec" strokeWidth="1" />
    </svg>
  );
}

function DbIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
      <ellipse cx="6" cy="3" rx="4" ry="1.4" fill="none" stroke="#a9b7c6" strokeWidth="1.2" />
      <path
        d="M2 3 V9 C2 9.8 3.8 10.4 6 10.4 C8.2 10.4 10 9.8 10 9 V3"
        fill="none"
        stroke="#a9b7c6"
        strokeWidth="1.2"
      />
      <path
        d="M2 6 C2 6.8 3.8 7.4 6 7.4 C8.2 7.4 10 6.8 10 6"
        fill="none"
        stroke="#a9b7c6"
        strokeWidth="1"
        opacity="0.7"
      />
    </svg>
  );
}

function ColumnIcon({ pk, nullable }: { pk: boolean; nullable: boolean }) {
  if (pk) {
    return (
      <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
        <circle cx="4.5" cy="6" r="2.4" fill="none" stroke="#bbb529" strokeWidth="1.4" />
        <line x1="6.9" y1="6" x2="11" y2="6" stroke="#bbb529" strokeWidth="1.4" />
        <line x1="9" y1="6" x2="9" y2="8" stroke="#bbb529" strokeWidth="1.4" />
      </svg>
    );
  }
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
      <circle
        cx="6"
        cy="6"
        r="2.2"
        fill={nullable ? 'none' : '#a9b7c6'}
        stroke="#a9b7c6"
        strokeWidth="1.2"
      />
    </svg>
  );
}

export function DatabaseSidebar({
  databases,
  activeDb,
  schema,
  schemaLoading,
  canManage,
  onSwitch,
  onCreate,
  onRename,
  onDelete,
  onExportDb,
  onRefresh,
  onPickTable,
  onSelectFromTable,
  onCountTable,
  onShowDdl,
  onDropTable,
  onInsertSnippet,
}: DatabaseSidebarProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [renamingDbName, setRenamingDbName] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [menu, setMenu] = useState<{
    pos: { x: number; y: number };
    items: ContextMenuItem[];
  } | null>(null);

  const beginRename = (name: string) => {
    setRenamingDbName(name);
    setRenameDraft(name);
  };

  const submitRename = () => {
    if (!renamingDbName) return;
    const next = sanitizeName(renameDraft);
    if (next && next !== renamingDbName) onRename(renamingDbName, next);
    setRenamingDbName(null);
    setRenameDraft('');
  };

  const openDbMenu = (e: React.MouseEvent, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({
      pos: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: 'Open',
          onClick: () => onSwitch(name),
          disabled: name === activeDb,
        },
        { separator: true, label: '' },
        {
          label: 'Rename…',
          onClick: () => beginRename(name),
          disabled: !canManage,
        },
        {
          label: 'Export .db…',
          onClick: () => onExportDb(name),
          disabled: !canManage,
        },
        { separator: true, label: '' },
        {
          label: 'Delete database…',
          onClick: () => {
            if (confirm(`Delete database "${name}"? This cannot be undone.`)) {
              onDelete(name);
            }
          },
          danger: true,
          disabled: !canManage,
        },
      ],
    });
  };

  const openTableMenu = (e: React.MouseEvent, table: SchemaTable) => {
    e.preventDefault();
    const isOpen = expanded.has(table.name);
    setMenu({
      pos: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: 'SELECT * FROM ' + table.name,
          onClick: () => onSelectFromTable(table.name),
        },
        {
          label: 'SELECT count(*)',
          onClick: () => onCountTable(table.name),
        },
        { separator: true, label: '' },
        {
          label: isOpen ? 'Hide columns' : 'Show columns',
          onClick: () => {
            if (isOpen) {
              setExpanded((prev) => {
                const next = new Set(prev);
                next.delete(table.name);
                return next;
              });
            } else {
              setExpanded((prev) => new Set(prev).add(table.name));
            }
          },
        },
        {
          label: 'Show DDL',
          onClick: () => onShowDdl(table),
          disabled: !table.ddl,
        },
        { separator: true, label: '' },
        {
          label: table.type === 'view' ? 'DROP VIEW…' : 'DROP TABLE…',
          onClick: () => onDropTable(table.name),
          danger: true,
        },
      ],
    });
  };

  const openColumnMenu = (e: React.MouseEvent, tableName: string, columnName: string) => {
    e.preventDefault();
    setMenu({
      pos: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: `Insert "${columnName}"`,
          onClick: () => onInsertSnippet(`"${columnName}"`),
        },
        {
          label: `Insert "${tableName}"."${columnName}"`,
          onClick: () => onInsertSnippet(`"${tableName}"."${columnName}"`),
        },
      ],
    });
  };

  const toggle = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const submitCreate = () => {
    const cleaned = sanitizeName(draftName);
    if (!cleaned) {
      setCreating(false);
      setDraftName('');
      return;
    }
    onCreate(cleaned);
    setCreating(false);
    setDraftName('');
  };

  return (
    <aside className="flex h-full w-full flex-col overflow-hidden border-r border-[#323232] bg-[#3c3f41]">
      <div className="flex shrink-0 items-center justify-between border-b border-[#323232] bg-[#3c3f41] px-2 py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#808080]">
          Databases {databases.length > 0 && `· ${databases.length}`}
        </span>
        {canManage && (
          <button
            type="button"
            onClick={() => setCreating((v) => !v)}
            className="rounded px-1 py-0.5 text-[14px] leading-none text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
            title="New database"
            aria-label="New database"
          >
            +
          </button>
        )}
      </div>

      <div className="max-h-[40%] shrink-0 overflow-auto bg-[#2b2b2b] py-1 text-[12px]">
        {databases.length === 0 && !creating && (
          <div className="px-2 py-1 text-[11px] italic text-[#606366]">No databases yet.</div>
        )}
        {databases.map((db) => {
          const isActive = db.name === activeDb;
          const isRenaming = renamingDbName === db.name;
          return (
            <div
              key={db.name}
              onContextMenu={(e) => openDbMenu(e, db.name)}
              className={
                'group flex items-center gap-1.5 px-2 py-0.5 ' +
                (isActive ? 'bg-[#214283] text-[#e8e8e8]' : 'text-[#bbbbbb] hover:bg-[#3c3f41]')
              }
            >
              <span className="shrink-0">
                <DbIcon />
              </span>
              {isRenaming ? (
                <input
                  autoFocus
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitRename();
                    if (e.key === 'Escape') {
                      setRenamingDbName(null);
                      setRenameDraft('');
                    }
                  }}
                  onBlur={submitRename}
                  className="min-w-0 flex-1 rounded border border-[#5394ec] bg-[#2b2b2b] px-1 py-0.5 font-mono text-[11px] text-[#e8e8e8] outline-none"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => onSwitch(db.name)}
                  onDoubleClick={() => canManage && beginRename(db.name)}
                  title={`${db.name}${isActive ? ' (active)' : ''} — right-click for actions`}
                  className="flex flex-1 items-center justify-between truncate text-left"
                >
                  <span className="truncate font-mono">{db.name}</span>
                  {isActive && db.byteLength > 0 && (
                    <span className="ml-2 shrink-0 text-[10px] tabular-nums text-[#808080]">
                      {db.byteLength < 1024 * 1024
                        ? `${(db.byteLength / 1024).toFixed(0)}K`
                        : `${(db.byteLength / 1024 / 1024).toFixed(1)}M`}
                    </span>
                  )}
                </button>
              )}
            </div>
          );
        })}
        {creating && (
          <div className="flex items-center gap-1.5 px-2 py-0.5">
            <span className="shrink-0">
              <DbIcon />
            </span>
            <input
              autoFocus
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitCreate();
                if (e.key === 'Escape') {
                  setCreating(false);
                  setDraftName('');
                }
              }}
              placeholder="new db name"
              className="min-w-0 flex-1 rounded border border-[#5394ec] bg-[#2b2b2b] px-1 py-0.5 font-mono text-[11px] text-[#e8e8e8] outline-none"
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-between border-y border-[#323232] bg-[#3c3f41] px-2 py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#808080]">
          Schema {schema.length > 0 && `· ${schema.length}`}
        </span>
        <button
          type="button"
          onClick={onRefresh}
          className="rounded px-1 py-0.5 text-[12px] leading-none text-[#bbbbbb] hover:bg-[#4c5052] hover:text-[#e8e8e8]"
          title="Refresh schema"
          aria-label="Refresh schema"
        >
          ↻
        </button>
      </div>

      <div className="flex-1 overflow-auto bg-[#2b2b2b] py-1 text-[12px]">
        {schemaLoading && <div className="px-2 py-1 text-[11px] text-[#606366]">Loading…</div>}
        {!schemaLoading && schema.length === 0 && (
          <div className="px-2 py-1 text-[11px] italic text-[#606366]">No tables yet.</div>
        )}
        {schema.map((table) => {
          const isOpen = expanded.has(table.name);
          return (
            <div key={table.name}>
              <div
                className="group flex items-center gap-1 px-1 py-0.5 hover:bg-[#3c3f41]"
                onContextMenu={(e) => openTableMenu(e, table)}
              >
                <button
                  type="button"
                  onClick={() => toggle(table.name)}
                  className="flex h-5 w-5 shrink-0 items-center justify-center text-[14px] leading-none text-[#a9b7c6] hover:text-[#e8e8e8]"
                  aria-label={isOpen ? 'Collapse' : 'Expand'}
                >
                  {isOpen ? '▾' : '▸'}
                </button>
                <span className="shrink-0">
                  <TableIcon kind={table.type} />
                </span>
                <button
                  type="button"
                  onClick={() => onPickTable(table.name)}
                  className="flex flex-1 items-center justify-between truncate text-left text-[#bbbbbb]"
                  title={`SELECT * FROM ${table.name} (right-click for more)`}
                >
                  <span className="truncate">{table.name}</span>
                  {table.rowCount != null && (
                    <span className="ml-2 shrink-0 text-[10px] tabular-nums text-[#606366]">
                      {table.rowCount.toLocaleString()}
                    </span>
                  )}
                </button>
              </div>
              {isOpen && (
                <ul className="bg-[#2b2b2b] pb-1">
                  {table.columns.map((col) => (
                    <li
                      key={col.name}
                      onContextMenu={(e) => openColumnMenu(e, table.name, col.name)}
                      onClick={() => onInsertSnippet(`"${col.name}"`)}
                      className="flex cursor-pointer items-center gap-1.5 truncate py-0.5 pl-7 pr-2 font-mono text-[11px] hover:bg-[#3c3f41]"
                      title={`${col.name} ${col.type || 'ANY'}${col.notNull ? ' NOT NULL' : ''}${col.pk ? ' PRIMARY KEY' : ''} (click to insert, right-click for more)`}
                    >
                      <ColumnIcon pk={col.pk > 0} nullable={!col.notNull} />
                      <span className="truncate text-[#bbbbbb]">{col.name}</span>
                      <span className="ml-auto shrink-0 truncate text-[10px] text-[#6a8759]">
                        {col.type || 'ANY'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      <ContextMenu
        position={menu?.pos ?? null}
        items={menu?.items ?? []}
        onClose={() => setMenu(null)}
      />
    </aside>
  );
}
